import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { WO_TYPE_COLORS, priorityColors } from '../utils/constants';
import { FiX, FiActivity, FiCheckCircle, FiAlertTriangle, FiBox, FiClock, FiChevronRight, FiDollarSign } from 'react-icons/fi';

const SLIDE_DURATION = 10000; // 10s per slide

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime() {
  return new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function formatDateHeader() {
  return new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function TVDisplay({ onClose }) {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [slide, setSlide] = useState(0);
  const [clock, setClock] = useState(formatTime());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const timerRef = useRef(null);

  const totalSlides = 6; // Welcome, Metrics, This Week, Upcoming, Completed, Costs
  const [costData, setCostData] = useState(null);

  const fetchData = useCallback(() => {
    const n = new Date(); const td = `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
    authFetch(`/api/stats/tv-display?today=${td}`).then(r => r.json()).then(setData).catch(() => {});
    authFetch('/api/stats/costs').then(r => r.ok ? r.json() : null).then(setCostData).catch(() => {});
  }, [authFetch]);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 60000); return () => clearInterval(i); }, [fetchData]);
  useEffect(() => { const i = setInterval(() => setClock(formatTime()), 1000); return () => clearInterval(i); }, []);

  // Track fullscreen state
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Auto-advance slides
  useEffect(() => {
    timerRef.current = setInterval(() => setSlide(s => (s + 1) % totalSlides), SLIDE_DURATION);
    return () => clearInterval(timerRef.current);
  }, []);

  const handleFullscreen = (e) => {
    e.stopPropagation();
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    onClose();
  };

  const statCards = data ? [
    { label: 'Active Jobs', value: data.stats.activeJobs, color: '#2563eb', icon: FiActivity },
    { label: 'Completed', value: data.stats.completedJobs, color: '#10b981', icon: FiCheckCircle },
    { label: 'Overdue', value: data.stats.overdueJobs, color: '#ef4444', icon: FiAlertTriangle },
    { label: 'Total Assets', value: data.stats.totalAssets, color: '#8b5cf6', icon: FiBox },
  ] : [];

  // Use server-provided overdue count (more accurate than client-side filter)
  const overdueCount = data?.stats?.overdueJobs || 0;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: '#0a0e1a',
      color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      overflow: 'hidden', cursor: 'none'
    }}
      onMouseMove={() => { document.body.style.cursor = 'default'; clearTimeout(window._tvCursor); window._tvCursor = setTimeout(() => { if (document.querySelector('[data-tv-display]')) document.body.style.cursor = 'none'; }, 3000); }}
      data-tv-display
    >
      {/* Animated background gradients */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute', width: '120vmax', height: '120vmax', top: '-30%', left: '-30%',
          background: 'radial-gradient(ellipse at 30% 20%, rgba(37,99,235,0.15) 0%, transparent 60%)',
          animation: 'tvFloat1 20s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute', width: '100vmax', height: '100vmax', bottom: '-20%', right: '-20%',
          background: 'radial-gradient(ellipse at 70% 80%, rgba(139,92,246,0.12) 0%, transparent 60%)',
          animation: 'tvFloat2 25s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute', width: '80vmax', height: '80vmax', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          background: 'radial-gradient(ellipse, rgba(16,185,129,0.08) 0%, transparent 70%)',
          animation: 'tvFloat3 18s ease-in-out infinite'
        }} />
      </div>

      {/* Top bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, padding: '16px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10,
        background: 'linear-gradient(180deg, rgba(10,14,26,0.9) 0%, transparent 100%)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/favicon.svg" alt="Asset Logic" style={{ width: 36, height: 36, borderRadius: 10 }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.5px' }}>Asset Logic</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>{formatDateHeader()}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 28, fontWeight: 300, fontVariantNumeric: 'tabular-nums', color: 'rgba(255,255,255,0.9)' }}>{clock}</div>
          <button onClick={handleClose} title="Close" style={{
            background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8,
            padding: 8, color: 'rgba(255,255,255,0.6)', cursor: 'pointer'
          }}>
            <FiX size={18} />
          </button>
        </div>
      </div>

      {/* Slide indicator dots */}
      <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, zIndex: 10 }}>
        {[...Array(totalSlides)].map((_, i) => (
          <button key={i} onClick={() => { setSlide(i); clearInterval(timerRef.current); timerRef.current = setInterval(() => setSlide(s => (s + 1) % totalSlides), SLIDE_DURATION); }}
            style={{
              width: slide === i ? 28 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer',
              background: slide === i ? '#2563eb' : 'rgba(255,255,255,0.25)',
              transition: 'all 0.4s ease'
            }}
          />
        ))}
      </div>

      {/* Progress bar for current slide */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, zIndex: 10, background: 'rgba(255,255,255,0.05)' }}>
        <div key={slide} style={{
          height: '100%', background: 'linear-gradient(90deg, #2563eb, #7c3aed)',
          animation: `tvProgress ${SLIDE_DURATION}ms linear forwards`, borderRadius: 2
        }} />
      </div>

      {/* Slides */}
      <AnimatePresence mode="wait">
        {slide === 0 && (
          <SlideWrapper key="welcome">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', padding: '80px 24px 60px' }}>
              <motion.img src="/favicon.svg" alt="Asset Logic"
                initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.8, ease: 'easeOut' }}
                style={{
                  width: 100, height: 100, borderRadius: 24, marginBottom: 24,
                  boxShadow: '0 20px 60px rgba(37,99,235,0.4)'
                }} />
              <motion.h1 initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3, duration: 0.6 }}
                style={{ fontSize: 'clamp(32px, 6vw, 56px)', fontWeight: 800, margin: '0 0 8px', letterSpacing: '-1px',
                  background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.7) 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                }}>Asset Logic</motion.h1>
              <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5, duration: 0.6 }}
                style={{ fontSize: 'clamp(14px, 2vw, 20px)', color: 'rgba(255,255,255,0.5)', fontWeight: 400, marginBottom: 40 }}>
                Asset Management System
              </motion.p>

              {/* Stats grid */}
              <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7, duration: 0.6 }}
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, width: '100%', maxWidth: 700 }}>
                {statCards.map((s, i) => (
                  <motion.div key={s.label} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.8 + i * 0.1 }}
                    style={{
                      padding: '20px 16px', borderRadius: 16, textAlign: 'center',
                      background: `linear-gradient(135deg, ${s.color}15, ${s.color}08)`,
                      border: `1px solid ${s.color}25`,
                      backdropFilter: 'blur(20px)'
                    }}>
                    <s.icon size={22} color={s.color} style={{ marginBottom: 8 }} />
                    <div style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 'clamp(10px, 1.5vw, 13px)', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginTop: 4 }}>{s.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </SlideWrapper>
        )}

        {slide === 1 && data && data.metrics && (
          <SlideWrapper key="metrics">
            <MetricsSlide metrics={data.metrics} weekStart={data.weekStart} weekEnd={data.weekEnd} lastWeekStart={data.lastWeekStart} lastWeekEnd={data.lastWeekEnd} />
          </SlideWrapper>
        )}

        {slide === 2 && data && (
          <SlideWrapper key="thisweek">
            <SlideContent
              title="This Week"
              subtitle={`${formatDate(data.weekStart)} — ${formatDate(data.weekEnd)}`}
              icon={FiClock}
              color="#f59e0b"
              items={data.thisWeek}
              emptyMsg="No work orders scheduled this week"
            />
          </SlideWrapper>
        )}

        {slide === 3 && data && (
          <SlideWrapper key="upcoming">
            <SlideContent
              title="Upcoming Work"
              subtitle="Next 7 days"
              icon={FiChevronRight}
              color="#6366f1"
              items={data.upcoming}
              emptyMsg="No upcoming work orders"
            />
          </SlideWrapper>
        )}

        {slide === 4 && data && (
          <SlideWrapper key="completed">
            <SlideContent
              title="Recently Completed"
              subtitle={`Completed this week (${formatDate(data.weekStart)} — ${formatDate(data.weekEnd)})`}
              icon={FiCheckCircle}
              color="#10b981"
              items={data.completed}
              emptyMsg="No completed work orders this week"
              isCompleted
            />
          </SlideWrapper>
        )}

        {slide === 5 && costData && (
          <SlideWrapper key="costs">
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '60px 80px' }}>
              <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
                style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 40 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(5,150,105,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiDollarSign size={24} color="#10b981" />
                </div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.5px' }}>Cost Overview</div>
                  <div style={{ fontSize: 14, color: '#94a3b8' }}>This month — {formatDate(costData.period?.start)} to {formatDate(costData.period?.end)}</div>
                </div>
              </motion.div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 40 }}>
                {[
                  { label: 'Total', value: `$${costData.total?.toLocaleString()}`, color: '#3b82f6' },
                  { label: 'Labour', value: `$${costData.labour?.toLocaleString()}`, color: '#059669' },
                  { label: 'Parts', value: `$${costData.parts?.toLocaleString()}`, color: '#f59e0b' },
                  { label: 'Services', value: `$${costData.services?.toLocaleString()}`, color: '#0ea5e9' },
                  { label: 'Ext. Labour', value: `$${costData.ext_labour?.toLocaleString()}`, color: '#8b5cf6' },
                ].map((c, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1 }}
                    style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: '24px 20px', border: `2px solid ${c.color}30`, textAlign: 'center' }}>
                    <div style={{ fontSize: 32, fontWeight: 800, color: c.color }}>{c.value}</div>
                    <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600, marginTop: 6, textTransform: 'uppercase', letterSpacing: '1px' }}>{c.label}</div>
                  </motion.div>
                ))}
              </div>
              {costData.breakdown?.length > 0 && (
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                  {costData.breakdown.map((d, i) => {
                    const pct = costData.total > 0 ? Math.round((d.value / costData.total) * 100) : 0;
                    return (
                      <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 + i * 0.1 }}
                        style={{ flex: `${pct} 0 0`, minWidth: 80, background: `${d.color}20`, borderRadius: 12, padding: '16px 14px', textAlign: 'center', border: `1px solid ${d.color}40` }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: d.color }}>{pct}%</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{d.name}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0', marginTop: 2 }}>${d.value.toLocaleString()}</div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
              {costData.total === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#64748b', fontSize: 18 }}>No cost data for this month</div>
              )}
            </div>
          </SlideWrapper>
        )}

      </AnimatePresence>
    </div>
  );
}

function SlideWrapper({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 60 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -60 }}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
      style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}
    >
      {children}
    </motion.div>
  );
}

function BarGraph({ label, value, max, color, delay }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay, duration: 0.5 }}
      style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 'clamp(11px, 1.3vw, 14px)', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>{label}</span>
        <span style={{ fontSize: 'clamp(11px, 1.3vw, 14px)', fontWeight: 700, color }}>{value}</span>
      </div>
      <div style={{ height: 10, borderRadius: 5, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ delay: delay + 0.2, duration: 1, ease: 'easeOut' }}
          style={{ height: '100%', borderRadius: 5, background: `linear-gradient(90deg, ${color}, ${color}99)` }} />
      </div>
    </motion.div>
  );
}

function AdherenceRing({ value, label, color, delay }) {
  const r = 54, c = 2 * Math.PI * r;
  const offset = c - (Math.min(value, 100) / 100) * c;
  return (
    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay, duration: 0.6 }}
      style={{ textAlign: 'center' }}>
      <svg width="130" height="130" viewBox="0 0 130 130" style={{ display: 'block', margin: '0 auto' }}>
        <circle cx="65" cy="65" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
        <motion.circle cx="65" cy="65" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeLinecap="round" strokeDasharray={c} initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: offset }} transition={{ delay: delay + 0.3, duration: 1.2, ease: 'easeOut' }}
          transform="rotate(-90 65 65)" />
        <text x="65" y="60" textAnchor="middle" fill="#fff" fontSize="28" fontWeight="800">{value}%</text>
        <text x="65" y="80" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="10" fontWeight="600" textTransform="uppercase">ADHERENCE</text>
      </svg>
      <div style={{ fontSize: 'clamp(11px, 1.3vw, 13px)', color: 'rgba(255,255,255,0.5)', fontWeight: 600, marginTop: 6 }}>{label}</div>
    </motion.div>
  );
}

function MetricsSlide({ metrics, weekStart, weekEnd, lastWeekStart, lastWeekEnd }) {
  const lw = metrics.lastWeek;
  const tw = metrics.thisWeek;
  const barMax = Math.max(lw.scheduled, lw.completed, lw.overdue, tw.scheduled, tw.completed, 1);

  return (
    <div style={{ padding: '80px 24px 60px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
        style={{ textAlign: 'center', marginBottom: 28, flexShrink: 0 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 20px', borderRadius: 30, background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)', marginBottom: 12 }}>
          <FiActivity size={18} color="#8b5cf6" />
          <span style={{ fontSize: 'clamp(14px, 2vw, 18px)', fontWeight: 700, color: '#8b5cf6' }}>Weekly Metrics</span>
        </div>
        <div style={{ fontSize: 'clamp(12px, 1.5vw, 15px)', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>Performance overview</div>
      </motion.div>

      <div style={{ flex: 1, display: 'flex', gap: 'clamp(16px, 3vw, 40px)', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', padding: '0 max(0px, calc(50% - 600px))' }}>
        {/* Adherence rings */}
        <div style={{ display: 'flex', gap: 'clamp(20px, 4vw, 50px)', justifyContent: 'center', flexShrink: 0 }}>
          <AdherenceRing value={lw.adherence} label={`Last Week (${formatDate(lastWeekStart)})`} color="#8b5cf6" delay={0.3} />
          <AdherenceRing value={tw.adherence} label={`This Week (${formatDate(weekStart)})`} color="#2563eb" delay={0.5} />
        </div>

        {/* Bar charts */}
        <div style={{ flex: 1, minWidth: 240, maxWidth: 420 }}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            style={{ fontSize: 'clamp(10px, 1vw, 12px)', fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12 }}>
            Last Week
          </motion.div>
          <BarGraph label="Scheduled" value={lw.scheduled} max={barMax} color="#6366f1" delay={0.5} />
          <BarGraph label="Completed" value={lw.completed} max={barMax} color="#10b981" delay={0.6} />
          <BarGraph label="Overdue" value={lw.overdue} max={barMax} color="#ef4444" delay={0.7} />
          <BarGraph label="Hours Confirmed" value={lw.hours} max={Math.max(lw.hours, tw.hours, 1)} color="#f59e0b" delay={0.8} />

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
            style={{ fontSize: 'clamp(10px, 1vw, 12px)', fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12, marginTop: 20 }}>
            This Week (so far)
          </motion.div>
          <BarGraph label="Scheduled" value={tw.scheduled} max={barMax} color="#6366f1" delay={1.0} />
          <BarGraph label="Completed" value={tw.completed} max={barMax} color="#10b981" delay={1.1} />
          <BarGraph label="Hours Confirmed" value={tw.hours} max={Math.max(lw.hours, tw.hours, 1)} color="#f59e0b" delay={1.2} />
        </div>
      </div>
    </div>
  );
}

function SlideContent({ title, subtitle, icon: Icon, color, items, emptyMsg, isCompleted }) {
  return (
    <div style={{ padding: '80px 24px 60px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
        style={{ textAlign: 'center', marginBottom: 24, flexShrink: 0 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 20px', borderRadius: 30, background: `${color}18`, border: `1px solid ${color}30`, marginBottom: 12 }}>
          <Icon size={18} color={color} />
          <span style={{ fontSize: 'clamp(14px, 2vw, 18px)', fontWeight: 700, color }}>{title}</span>
        </div>
        <div style={{ fontSize: 'clamp(12px, 1.5vw, 15px)', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>{subtitle}</div>
      </motion.div>

      {(!items || items.length === 0) ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 'clamp(14px, 2vw, 18px)', color: 'rgba(255,255,255,0.3)' }}>{emptyMsg}</div>
        </div>
      ) : (
        <div style={{
          flex: 1, overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
          gap: 10, alignContent: 'start', padding: '0 max(0px, calc(50% - 700px))'
        }}>
          {items.slice(0, 12).map((wo, idx) => (
            <motion.div key={wo.id} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 + idx * 0.06 }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px', borderRadius: 12,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(10px)'
              }}>
              <div style={{ flexShrink: 0 }}>
                <div style={{
                  padding: '4px 8px', borderRadius: 6, fontSize: 'clamp(9px, 1vw, 11px)', fontWeight: 700,
                  background: `${WO_TYPE_COLORS[wo.type] || '#6b7280'}25`,
                  color: WO_TYPE_COLORS[wo.type] || '#6b7280'
                }}>{wo.type}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'clamp(12px, 1.4vw, 14px)', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {wo.wo_number} — {wo.name}
                </div>
                {wo.asset_name && (
                  <div style={{ fontSize: 'clamp(10px, 1.2vw, 12px)', color: 'rgba(255,255,255,0.4)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {wo.asset_tag && <span style={{ fontWeight: 600 }}>{wo.asset_tag}</span>}{wo.asset_tag && ' — '}{wo.asset_name}
                  </div>
                )}
                {wo.assigned_to_name && (
                  <div style={{ fontSize: 'clamp(10px, 1.1vw, 11px)', color: 'rgba(96,165,250,0.7)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Assigned: {wo.assigned_to_name}
                  </div>
                )}
              </div>
              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                {!isCompleted && wo.due_date && (
                  <div style={{ fontSize: 'clamp(10px, 1vw, 12px)', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
                    {formatDate(wo.due_date)}
                  </div>
                )}
                {isCompleted && wo.completed_at && (
                  <div style={{ fontSize: 'clamp(10px, 1vw, 12px)', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
                    {formatDate(wo.completed_at)}
                  </div>
                )}
                <div style={{
                  fontSize: 'clamp(9px, 0.9vw, 10px)', fontWeight: 700, marginTop: 2,
                  padding: '2px 6px', borderRadius: 4, display: 'inline-block',
                  background: wo.status === 'Complete' || wo.status === 'TECO' ? 'rgba(16,185,129,0.15)' :
                    wo.status === 'In Progress' ? 'rgba(37,99,235,0.15)' : 'rgba(255,255,255,0.08)',
                  color: wo.status === 'Complete' || wo.status === 'TECO' ? '#10b981' :
                    wo.status === 'In Progress' ? '#60a5fa' : 'rgba(255,255,255,0.5)'
                }}>
                  {wo.status}
                </div>
                {!isCompleted && wo.op_count > 0 && (
                  <div style={{ fontSize: 'clamp(9px, 0.8vw, 10px)', color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>
                    {wo.op_done}/{wo.op_count} ops done
                  </div>
                )}
                {wo.priority && wo.priority !== 'Normal' && (
                  <div style={{ fontSize: 'clamp(8px, 0.8vw, 10px)', fontWeight: 700, marginTop: 2, color: priorityColors[wo.priority] || '#6b7280' }}>
                    {wo.priority}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
